import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowUpRight } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface DailyPaymentsWidgetProps {
  totalPayments: number;
  paymentCount: number;
  isLoading?: boolean;
  title?: string;
  subtitle?: string;
}

const DailyPaymentsWidget: React.FC<DailyPaymentsWidgetProps> = ({
  totalPayments,
  paymentCount,
  isLoading,
  title = "Total Outgoing",
  subtitle = "Payments made to vendors"
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (isLoading) {
    return (
      <Card className="relative overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 sm:pb-3">
          <CardTitle className="text-xs sm:text-sm font-medium">{title}</CardTitle>
          <Skeleton className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg" />
        </CardHeader>
        <CardContent className="pt-2">
          <Skeleton className="h-6 sm:h-8 w-24 sm:w-32 mb-1" />
          <Skeleton className="h-3 sm:h-4 w-20 sm:w-24" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="relative overflow-hidden border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-200 bg-white dark:bg-gray-900">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-orange-500"></div>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 sm:pb-3">
        <div className="min-w-0 flex-1">
          <CardTitle className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">{title}</CardTitle>
          <p className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-500 mt-0.5 sm:mt-1 truncate">{subtitle}</p>
        </div>
        <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg bg-red-50 dark:bg-red-950/30 flex items-center justify-center flex-shrink-0 ml-2">
          <ArrowUpRight className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 dark:text-red-400" />
        </div>
      </CardHeader>
      <CardContent className="pt-2">
        <div className="text-lg sm:text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight">
          {formatCurrency(totalPayments)}
        </div>
        <div className="flex items-center text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-1 sm:mt-2">
          <div className="flex items-center">
            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full mr-1.5 sm:mr-2"></div>
            <span className="font-medium">{paymentCount}</span>
            <span className="text-gray-500 dark:text-gray-500 ml-1">payments made</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default DailyPaymentsWidget;